import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'

export interface PageStat {
  path: string
  today: string
  last7d: string
}

export const PATH_LABELS: Record<string, string> = {
  '/': 'Startseite',
  '/brunch': 'Brunch Übersicht',
  '/galerie': 'Galerie',
  '/kontakt': 'Kontakt',
  '/geschichte': 'Geschichte',
  '/impressum': 'Impressum',
  '/datenschutz': 'Datenschutz',
  '/jobs': 'Jobs',
  '/veranstaltung': 'Veranstaltung',
  '/veranstaltungen': 'Veranstaltungen',
  '/raeume': 'Räume',
  '/partys': 'Partys Übersicht',
  '/partys-karneval': 'Partys Karneval',
  '/hochzeiten': 'Hochzeiten',
  '/unternehmensfeiern': 'Unternehmensfeiern',
  '/private-feiern': 'Private Feiern',
  '/buffets-angebote': 'Buffets & Angebote',
  '/foodtruck': 'Foodtruck',
}

export function getPageLabel(path: string): string {
  const normalized = path.replace(/^\//, '/') || '/'
  if (path.startsWith('/partys/') && path.length > 7) return `Event: ${path.replace('/partys/', '')}`
  if (path.startsWith('/brunch/') && path.length > 8) return `Brunch-Detail: ${path.replace('/brunch/', '')}`
  return PATH_LABELS[normalized] ?? (normalized === '/' ? 'Startseite' : normalized)
}

export function StatTable({
  rows,
  maxBar,
  startIndex,
}: {
  rows: PageStat[]
  maxBar: number
  startIndex: number
}) {
  return (
    <table className="w-full min-w-[520px] sm:min-w-0">
      <thead>
        <tr className="text-left text-gray-400 text-xs sm:text-sm border-b border-white/5">
          <th className="px-3 py-3 sm:px-6 sm:py-4 font-medium">Seite</th>
          <th className="px-2 py-3 sm:px-6 sm:py-4 font-medium text-right w-20 sm:w-28">Heute</th>
          <th className="px-2 py-3 sm:px-6 sm:py-4 font-medium text-right w-24 sm:w-32">7 Tage</th>
          <th className="px-2 py-3 sm:px-6 sm:py-4 font-medium w-32 sm:w-48 pl-2 sm:pl-4">Anteil</th>
          <th className="px-2 py-3 sm:px-6 sm:py-4 w-16 sm:w-20" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const last7dNum = parseInt(row.last7d, 10) || 0
          const share = maxBar > 0 ? (last7dNum / maxBar) * 100 : 0
          const pathForLink = (row.path && row.path !== '/') ? row.path : '/'
          return (
            <motion.tr
              key={row.path + index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.03 * (startIndex + index) }}
              className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
            >
              <td className="px-3 py-3 sm:px-6 sm:py-4 min-w-[120px]">
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-white text-sm sm:text-base truncate max-w-[180px] sm:max-w-none">{getPageLabel(row.path)}</span>
                  <span className="text-gray-500 text-xs font-mono truncate max-w-[180px] sm:max-w-none">{row.path || '/'}</span>
                </div>
              </td>
              <td className="px-2 py-3 sm:px-6 sm:py-4 text-right whitespace-nowrap">
                <span className="text-white font-semibold tabular-nums text-sm sm:text-base">{row.today}</span>
              </td>
              <td className="px-2 py-3 sm:px-6 sm:py-4 text-right whitespace-nowrap">
                <span className="text-brand-400 font-semibold tabular-nums text-sm sm:text-base">{row.last7d}</span>
              </td>
              <td className="px-2 py-3 sm:px-6 sm:py-4 pl-2 sm:pl-4">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden max-w-[100px] sm:max-w-[140px]">
                  <motion.div
                    className="h-full bg-gradient-to-r from-brand-500 to-brand-400 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${share}%` }}
                    transition={{ duration: 0.5, delay: 0.05 * (startIndex + index) }}
                  />
                </div>
              </td>
              <td className="px-2 py-3 sm:px-6 sm:py-4">
                <Link
                  to={pathForLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-gray-400 hover:text-brand-400 text-xs sm:text-sm transition-colors min-h-[44px] min-w-[44px] items-center justify-center touch-manipulation"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline">Öffnen</span>
                </Link>
              </td>
            </motion.tr>
          )
        })}
      </tbody>
    </table>
  )
}
